import { QuoteStatus } from '@/types';
import type { Quote } from '@/types';
import { isOpenQuoteStatus } from './quoteMeta';
import { getSignatureOverview } from './signatureStatus';

/**
 * De signalen achter de balk 'Vraagt actie'.
 *
 * Het overzicht heeft twee verschillende vragen te beantwoorden: 'waar staat
 * deze offerte in het proces' (dat zijn de statustabs) en 'waar moet ik vandaag
 * iets mee'. Dat tweede loopt dwars door de statussen heen, dus krijgt het een
 * eigen filterlaag in plaats van nog een tab.
 */

export type QuoteSignal = 'expiring' | 'neverOpened' | 'awaitingUbo' | 'ghost' | 'outdated';

export const QUOTE_SIGNALS: QuoteSignal[] = ['expiring', 'neverOpened', 'awaitingUbo', 'ghost', 'outdated'];

export const SIGNAL_LABELS: Record<QuoteSignal, string> = {
  expiring: 'Verloopt < 7 dagen',
  neverOpened: 'Nooit geopend',
  awaitingUbo: 'Wacht op UBO',
  ghost: 'Spookoffertes',
  outdated: 'Oude productversie',
};

export const SIGNAL_HINTS: Record<QuoteSignal, string> = {
  expiring: 'Openstaande offertes die binnen zeven dagen verlopen.',
  neverOpened: 'Verstuurd, maar door de klant nog nooit geopend.',
  awaitingUbo: 'Klant heeft getekend, de UBO-handtekening ontbreekt nog.',
  ghost: 'Concepten die na het aanmaken nooit zijn geopend of bewerkt.',
  outdated: 'Bevat regels die op een oudere productversie staan.',
};

const DAY_MS = 24 * 60 * 60 * 1000;
export const EXPIRY_WARNING_DAYS = 7;

/** Hele dagen tot de vervaldatum; negatief als de offerte al verlopen is. */
export function daysUntilExpiry(quote: Quote, now: number = Date.now()) {
  if (!quote.expiresAt) return null;
  return Math.ceil((new Date(quote.expiresAt).getTime() - now) / DAY_MS);
}

export function isExpiringSoon(quote: Quote, now: number = Date.now()) {
  if (!isOpenQuoteStatus(quote.status)) return false;
  const days = daysUntilExpiry(quote, now);
  return days !== null && days >= 0 && days <= EXPIRY_WARNING_DAYS;
}

export function isNeverOpened(quote: Quote) {
  return quote.status === QuoteStatus.SENT && (quote.viewCount ?? 0) === 0;
}

export function needsUboCountersign(quote: Quote) {
  return Boolean(quote.signatureSummary?.countersignRequired);
}

export function isFullySigned(quote: Quote) {
  return getSignatureOverview(quote).everyoneSigned;
}

export function hasOutdatedRows(quote: Quote) {
  return (quote.outdatedProductRows ?? 0) > 0;
}

export function matchesSignal(quote: Quote, signal: QuoteSignal, now: number = Date.now()) {
  switch (signal) {
    case 'expiring': return isExpiringSoon(quote, now);
    case 'neverOpened': return isNeverOpened(quote);
    case 'awaitingUbo': return needsUboCountersign(quote);
    case 'ghost': return quote.isGhost === true;
    case 'outdated': return hasOutdatedRows(quote);
    default: return false;
  }
}

export function countSignals(quotes: Quote[], now: number = Date.now()) {
  const counts: Record<QuoteSignal, number> = {
    expiring: 0, neverOpened: 0, awaitingUbo: 0, ghost: 0, outdated: 0,
  };
  for (const quote of quotes) {
    for (const signal of QUOTE_SIGNALS) {
      if (matchesSignal(quote, signal, now)) counts[signal]++;
    }
  }
  return counts;
}

/**
 * De vervaldatum als leesbare tekst, plus of hij aandacht vraagt. De lijst en de
 * kaart tonen hem anders (kolom versus regel), maar moeten hetzelfde rekenen.
 */
export function describeExpiry(quote: Quote, now: number = Date.now()) {
  if (!quote.expiresAt) return { label: '—', urgent: false };
  const days = daysUntilExpiry(quote, now);
  const date = new Date(quote.expiresAt).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  if (days === null) return { label: '—', urgent: false };
  if (days < 0) return { label: `verlopen op ${date}`, urgent: false };
  if (!isOpenQuoteStatus(quote.status)) return { label: date, urgent: false };
  if (days === 0) return { label: 'verloopt vandaag', urgent: true };
  if (days === 1) return { label: 'verloopt morgen', urgent: true };
  if (days <= EXPIRY_WARNING_DAYS) return { label: `over ${days} dagen`, urgent: true };
  return { label: date, urgent: false };
}
